import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import apiService from '../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [formData, setFormData] = useState({
    prefix: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    language: '',
    profilePhoto: null,
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    bloodGroup: '',
    mobileNumber: '',
    alternateContact: '',
    familyContact: '',
    facebookLink: '',
    twitterLink: '',
    socialMedia1: '',
    socialMedia2: '',
    customField1: '',
    customField2: '',
    customField3: '',
    customField4: '',
    guardianName: '',
    idProofName: '',
    idProofNumber: '',
    permanentAddress: '',
    currentAddress: '',
    accountHolderName: '',
    accountNumber: '',
    bankName: '',
    bankIdentifierCode: '',
    branch: '',
    taxPayerId: ''
  });

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const user = await apiService.getCurrentUser();
        if (user) {
          setFormData(prev => ({
            ...prev,
            prefix: user.prefix || 'Mr',
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            email: user.email || '',
            mobileNumber: user.mobile || '',
            profilePhoto: user.profile_photo || null
          }));
          if (user.profile_photo) {
            setImagePreview(user.profile_photo);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadUserProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setFormData(prev => ({ ...prev, profilePhoto: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New password and confirm password do not match');
      return;
    }
    
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      alert('Please fill in all password fields');
      return;
    }

    setIsLoading(true);
    try {
      await apiService.changePassword(passwordData);
      setShowSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          if (key === 'profilePhoto' && formData[key] instanceof File) {
            formDataToSend.append('profile_photo', formData[key]);
          } else if (key !== 'profilePhoto') {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      await apiService.updateUserProfile(formDataToSend);
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="custom-profile-page">
      <div className="profile-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1>My Profile</h1>
      </div>

      {showSuccess && (
        <div className="success-message">
          Updated successfully!
        </div>
      )}

      <form onSubmit={handlePasswordSubmit} className="profile-form">
        <div className="form-section">
          <h2>Change Password</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Current password:</label>
              <div className="password-input-wrapper">
                <svg className="lock-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Current password"
                />
              </div>
            </div>
            <div className="form-group">
              <label>New password:</label>
              <div className="password-input-wrapper">
                <svg className="lock-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="New password"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Confirm new password:</label>
              <div className="password-input-wrapper">
                <svg className="lock-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="update-button" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-section">
          <h2>Edit Profile</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Prefix:</label>
              <select name="prefix" value={formData.prefix} onChange={handleChange}>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
                <option value="Dr">Dr</option>
              </select>
            </div>
            <div className="form-group">
              <label>First Name:</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First Name"
              />
            </div>
            <div className="form-group">
              <label>Last Name:</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last Name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="leftyliqour@gmail.com"
              />
            </div>
            <div className="form-group">
              <label>Language:</label>
              <input
                type="text"
                name="language"
                value={formData.language}
                onChange={handleChange}
                placeholder="Language"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Profile Photo</label>
              <div className="file-upload-wrapper">
                <input
                  type="file"
                  id="profilePhoto"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="profilePhoto" className="file-upload-label">
                  Upload Image: {formData.profilePhoto ? formData.profilePhoto.name || 'Current photo' : 'No file chosen'}
                </label>
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Profile preview" />
                  </div>
                )}
              </div>
              <small>Max File size: 5MB</small>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>More Informations</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date of birth:</label>
              <input
                type="text"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                placeholder="Date of birth"
              />
            </div>
            <div className="form-group">
              <label>Gender:</label>
              <select name="gender" value={formData.gender} onChange={handleChange}>
                <option value="">Please Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Marital Status:</label>
              <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
                <option value="">Marital Status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Blood Group:</label>
              <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}>
                <option value="">Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mobile Number:</label>
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                placeholder="Mobile Number"
              />
            </div>
            <div className="form-group">
              <label>Alternate contact number:</label>
              <input
                type="tel"
                name="alternateContact"
                value={formData.alternateContact}
                onChange={handleChange}
                placeholder="Alternate contact number"
              />
            </div>
            <div className="form-group">
              <label>Family contact number:</label>
              <input
                type="tel"
                name="familyContact"
                value={formData.familyContact}
                onChange={handleChange}
                placeholder="Family contact number"
              />
            </div>
            <div className="form-group">
              <label>Facebook Link:</label>
              <input
                type="text"
                name="facebookLink"
                value={formData.facebookLink}
                onChange={handleChange}
                placeholder="Facebook Link"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Twitter Link:</label>
              <input
                type="text"
                name="twitterLink"
                value={formData.twitterLink}
                onChange={handleChange}
                placeholder="Twitter Link"
              />
            </div>
            <div className="form-group">
              <label>Social Media 1:</label>
              <input
                type="text"
                name="socialMedia1"
                value={formData.socialMedia1}
                onChange={handleChange}
                placeholder="Social Media 1"
              />
            </div>
            <div className="form-group">
              <label>Social Media 2:</label>
              <input
                type="text"
                name="socialMedia2"
                value={formData.socialMedia2}
                onChange={handleChange}
                placeholder="Social Media 2"
              />
            </div>
            <div className="form-group">
              <label>Custom field 1:</label>
              <input
                type="text"
                name="customField1"
                value={formData.customField1}
                onChange={handleChange}
                placeholder="Custom field 1"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Custom field 2:</label>
              <input
                type="text"
                name="customField2"
                value={formData.customField2}
                onChange={handleChange}
                placeholder="Custom field 2"
              />
            </div>
            <div className="form-group">
              <label>Guardian Name:</label>
              <input
                type="text"
                name="guardianName"
                value={formData.guardianName}
                onChange={handleChange}
                placeholder="Guardian Name"
              />
            </div>
            <div className="form-group">
              <label>ID proof name:</label>
              <input
                type="text"
                name="idProofName"
                value={formData.idProofName}
                onChange={handleChange}
                placeholder="ID proof name"
              />
            </div>
            <div className="form-group">
              <label>ID proof number:</label>
              <input
                type="text"
                name="idProofNumber"
                value={formData.idProofNumber}
                onChange={handleChange}
                placeholder="ID proof number"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Permanent Address:</label>
              <textarea
                name="permanentAddress"
                value={formData.permanentAddress}
                onChange={handleChange}
                placeholder="Permanent Address"
                rows="3"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Current Address:</label>
              <textarea
                name="currentAddress"
                value={formData.currentAddress}
                onChange={handleChange}
                placeholder="Current Address"
                rows="3"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Bank Details:</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Account Holder's Name:</label>
              <input
                type="text"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleChange}
                placeholder="Account Holder's Name"
              />
            </div>
            <div className="form-group">
              <label>Account Number:</label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                placeholder="Account Number"
              />
            </div>
            <div className="form-group">
              <label>Bank Name:</label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                placeholder="Bank Name"
              />
            </div>
            <div className="form-group">
              <label>Bank Identifier Code:</label>
              <input
                type="text"
                name="bankIdentifierCode"
                value={formData.bankIdentifierCode}
                onChange={handleChange}
                placeholder="Bank Identifier Code"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Branch:</label>
              <input
                type="text"
                name="branch"
                value={formData.branch}
                onChange={handleChange}
                placeholder="Branch"
              />
            </div>
            <div className="form-group">
              <label>Tax Payer ID:</label>
              <input
                type="text"
                name="taxPayerId"
                value={formData.taxPayerId}
                onChange={handleChange}
                placeholder="Tax Payer ID"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="update-button" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Profile;
